import { CSSProperties, FC, memo, ReactNode } from 'react';
import Skeleton from 'react-loading-skeleton';
import './style.scss';
import moment from 'moment';
import { BALANCE_TYPE, BALANCE_TYPE_TXT, TRANSACTION_TYPE, TRANSACTION_TYPE_TXT } from '../../constants/walletTransactionRow';
import { getWTLogColumnsSceleton, IWTLogColumnsWidth } from '../../helpers/walletTransaction';
import { IWalletTransaction } from '../../store/walletTransactionStore';
import { localeStore } from '~/mobx/localesStore';

const renderSkeleton = (width: number = 100, height: number = 19.2, style?: CSSProperties) => {
  return <Skeleton width={width} height={height} style={style} />;
};

const getBalanceTypeTxt = (balance_type: BALANCE_TYPE | null) => {
  if (balance_type === null) {
    return '-';
  }

  return localeStore.t(BALANCE_TYPE_TXT[balance_type]);
};

const getTransactionTypeTxt = (transaction_type: TRANSACTION_TYPE | null) => {
  if (transaction_type === null) {
    return '-';
  }

  return localeStore.t(TRANSACTION_TYPE_TXT[transaction_type]);
};

const getBusinessDateFormat = (date: string | null) => {
  if (date === null) {
    return '-';
  }

  const formattedDate = moment(date, 'YYYY-MM-DD HH:mm:ss').format('DD/MM/YYYY, h:mm:ss A');
  const [dateStr, timeStr] = formattedDate.split(', ');

  return (
    <>
      {dateStr},
      <br />
      {timeStr}
    </>
  );
};

const getName = (name: string | null | undefined, phone: string | null | undefined) => {
  if (name && phone) {
    return (
      <>
        {name},
        <br />
        {phone}
      </>
    );
  }

  if (name) {
    return name;
  }

  if (phone) {
    return phone;
  }

  return '-';
};

const getAmount = (amount: number, currency: string) => {
  return `${currency} ${amount}`;
};

const getCellText = (txt: any) => {
  if (txt === null) {
    return '-';
  }

  if (txt.toString().length === 0) {
    return '-';
  }

  return txt;
};

interface IProps {
  walletTransaction?: IWalletTransaction;
  columnsWidth: IWTLogColumnsWidth;
}

const RowCell: FC<{ children: ReactNode; width: number; className?: string; onClick?: () => void }> = ({
  children,
  width,
  className = '',
  onClick,
}): JSX.Element => {
  return (
    <div className={`cell w-${width} ${onClick ? 'cursor-pointer' : ''} ${className}`} onClick={onClick}>
      {children}
    </div>
  );
};

const WTLogListRow: FC<IProps> = ({ walletTransaction, columnsWidth }: IProps): JSX.Element => {
  const defaultClasses = 'family-medium text-size-16 text-elipsis';

  const depositDate = getBusinessDateFormat(walletTransaction?.deposit_date ?? null);
  const author = getName(walletTransaction?.author_name, walletTransaction?.author_phone);
  const object = getName(walletTransaction?.object, walletTransaction?.object_phone);
  const amount = getAmount(walletTransaction?.transaction_amount ?? 0, walletTransaction?.transaction_currency ?? '');
  const balance_type = getBalanceTypeTxt(walletTransaction?.balance_type ?? null);
  const transaction_type = getTransactionTypeTxt(walletTransaction?.transaction_type ?? null);
  const reason = getCellText(walletTransaction?.reason ?? null);
  const order_number = getCellText(walletTransaction?.order_number ?? null);
  const expirationDate = getBusinessDateFormat(walletTransaction?.expiration_date ?? null);
  const wallet_id = getCellText(walletTransaction?.wallet_id ?? null);

  return (
    <div className="wallet-transaction-list-row">
      <RowCell width={columnsWidth.depositDate}>
        {walletTransaction ? <p className={defaultClasses}>{depositDate}</p> : renderSkeleton(getWTLogColumnsSceleton(columnsWidth.depositDate))}
      </RowCell>

      <RowCell width={columnsWidth.author_name}>
        {walletTransaction ? <p className={defaultClasses}>{author}</p> : renderSkeleton(getWTLogColumnsSceleton(columnsWidth.author_name))}
      </RowCell>

      <RowCell width={columnsWidth.object}>
        {walletTransaction ? <p className={defaultClasses}>{object}</p> : renderSkeleton(getWTLogColumnsSceleton(columnsWidth.object))}
      </RowCell>

      <RowCell width={columnsWidth.transaction_amount}>
        {walletTransaction ? <p className={defaultClasses}>{amount}</p> : renderSkeleton(getWTLogColumnsSceleton(columnsWidth.transaction_amount))}
      </RowCell>

      <RowCell width={columnsWidth.balance_type}>
        {walletTransaction ? <p className={defaultClasses}>{balance_type}</p> : renderSkeleton(getWTLogColumnsSceleton(columnsWidth.balance_type))}
      </RowCell>

      <RowCell width={columnsWidth.transaction_type}>
        {walletTransaction ? (
          <p className={defaultClasses}>{transaction_type}</p>
        ) : (
          renderSkeleton(getWTLogColumnsSceleton(columnsWidth.transaction_type))
        )}
      </RowCell>

      <RowCell width={columnsWidth.reason}>
        {walletTransaction ? <p className={defaultClasses}>{reason}</p> : renderSkeleton(getWTLogColumnsSceleton(columnsWidth.reason))}
      </RowCell>

      <RowCell width={columnsWidth.order_number}>
        {walletTransaction ? <p className={defaultClasses}>{order_number}</p> : renderSkeleton(getWTLogColumnsSceleton(columnsWidth.order_number))}
      </RowCell>

      <RowCell width={columnsWidth.expiration_date}>
        {walletTransaction ? (
          <p className={defaultClasses}>{expirationDate}</p>
        ) : (
          renderSkeleton(getWTLogColumnsSceleton(columnsWidth.expiration_date))
        )}
      </RowCell>

      <RowCell width={columnsWidth.wallet_id}>
        {walletTransaction ? <p className={defaultClasses}>{wallet_id}</p> : renderSkeleton(getWTLogColumnsSceleton(columnsWidth.wallet_id))}
      </RowCell>
    </div>
  );
};

export default memo(WTLogListRow);
